import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FileQuestion, ListChecks, PlusCircle, Timer, Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { FilterBar, type FilterConfig } from '@/components/ui/FilterBar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useQuizzes, useDeleteQuiz } from '@/hooks/useQuizzes'
import { useCoursesByIds } from '@/hooks/useCourses'
import { QuizModel } from '@/models/quiz.model'
import { paths } from '@/routes/path'
import { useNotifications } from '@/contexts/NotificationContext'

type QuizFilters = {
  search?: string
  status?: 'active' | 'inactive' | ''
  courseId?: string
}

const filterConfig: FilterConfig[] = [
  {
    key: 'search',
    type: 'search',
    label: 'Search by title or course',
    placeholder: 'Search quizzes',
  },
  {
    key: 'status',
    type: 'select',
    label: 'Availability',
    options: [
      { label: 'All quizzes', value: '' },
      { label: 'Published', value: 'active' },
      { label: 'Hidden', value: 'inactive' },
    ],
  },
]

export function QuizManagementPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const courseIdFromParams = searchParams.get('courseId') ?? undefined
  const courseTitleFromParams = searchParams.get('courseTitle') ?? undefined
  const { addNotification } = useNotifications()
  const [filters, setFilters] = useState<QuizFilters>(() => ({
    search: '',
    status: '',
    courseId: courseIdFromParams,
  }))
  const [quizToDelete, setQuizToDelete] = useState<QuizModel | null>(null)
  useEffect(() => {
    setFilters((prev) =>
      prev.courseId === courseIdFromParams
        ? prev
        : {
            ...prev,
            courseId: courseIdFromParams,
          },
    )
  }, [courseIdFromParams])

  const { data, isLoading, error } = useQuizzes({ perPage: 200 })
  const deleteQuiz = useDeleteQuiz()

  const quizzes = data?.quizzes ?? []

  const courseIds = useMemo(() => quizzes.map((quiz) => quiz.courseId), [quizzes])
  const { data: courseLookup } = useCoursesByIds(courseIds)

  const filteredQuizzes = useMemo(() => {
    const searchTerm = filters.search?.trim().toLowerCase()
    const status = filters.status

    return quizzes.filter((quiz) => {
      const courseTitle = courseLookup?.[quiz.courseId]?.title ?? ''
      const matchesSearch =
        !searchTerm ||
        quiz.title.toLowerCase().includes(searchTerm) ||
        courseTitle.toLowerCase().includes(searchTerm)

      const matchesStatus =
        !status || (status === 'active' ? quiz.isActive : !quiz.isActive)
      const matchesCourse = !filters.courseId || quiz.courseId === filters.courseId

      return matchesSearch && matchesStatus && matchesCourse
    })
  }, [quizzes, filters.search, filters.status, filters.courseId, courseLookup])

  const activeCount = useMemo(() => quizzes.filter((quiz) => quiz.isActive).length, [quizzes])
  const filteredCourseTitle =
    (filters.courseId && courseLookup?.[filters.courseId]?.title) || courseTitleFromParams

  const handleFiltersChange = (values: Record<string, any>) => {
    setFilters((prev) => ({
      ...prev,
      search: values.search ?? '',
      status: (values.status as QuizFilters['status']) ?? '',
    }))
  }

  const clearCourseFilter = () => {
    setFilters((prev) => ({
      ...prev,
      courseId: undefined,
    }))
    const next = new URLSearchParams(searchParams)
    next.delete('courseId')
    next.delete('courseTitle')
    setSearchParams(next)
  }

  const handleDelete = async () => {
    if (!quizToDelete) return
    const target = quizToDelete
    try {
      await deleteQuiz.mutateAsync(target.id)
      addNotification({
        title: 'Quiz removed',
        message: `"${target.title}" has been deleted.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })
    } catch (mutationError) {
      console.error(mutationError)
      addNotification({
        title: 'Unable to delete quiz',
        message: 'Please try again or contact the platform administrator.',
        type: 'general',
        priority: 'high',
        isRead: false,
        isArchived: false,
      })
    }
    setQuizToDelete(null)
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-lg border-red-100 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              Something went wrong
            </CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not load the quiz list. Refresh the page or try again later.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-medium text-red-600">
            <FileQuestion className="h-4 w-4" />
            Instructor workspace
          </p>
          <h1 className="text-3xl font-bold text-gray-900">Quiz manager</h1>
          <p className="max-w-3xl text-gray-600">
            Build and organise course quizzes, manage availability, and review learner activity.
            Keep your assessments structured and friendly for learners.
          </p>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={() => navigate(paths.app.quizzes.create.absolute)}
        >
          <PlusCircle className="h-4 w-4" />
          Create quiz
        </Button>
      </header>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={<ListChecks className="h-5 w-5 text-primary" />}
          title="Total quizzes"
          value={quizzes.length}
        />
        <StatTile
          icon={<Users className="h-5 w-5 text-green-600" />}
          title="Published quizzes"
          value={activeCount}
          tone="success"
        />
        <StatTile
          icon={<Timer className="h-5 w-5 text-blue-600" />}
          title="With timers"
          value={quizzes.filter((quiz) => (quiz.timeLimitSeconds ?? 0) > 0).length}
          tone="info"
        />
        <StatTile
          icon={<FileQuestion className="h-5 w-5 text-amber-600" />}
          title="Awaiting activation"
          value={quizzes.filter((quiz) => !quiz.isActive).length}
          tone="warning"
        />
      </section>

      {filters.courseId && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <span>
            Showing quizzes for{' '}
            <span className="font-semibold">
              {filteredCourseTitle || 'selected course'}
            </span>
            .
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-primary-700 hover:text-primary-800"
            onClick={clearCourseFilter}
          >
            Clear filter
          </Button>
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <FilterBar filters={filterConfig} onFiltersChange={handleFiltersChange} />
        </div>
        <div className="divide-y divide-gray-100">
          {filteredQuizzes.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <FileQuestion className="h-10 w-10 text-gray-300" />
              <p className="text-lg font-semibold text-gray-700">No quizzes found</p>
              <p className="max-w-md text-sm text-gray-500">
                Adjust your filters or create a new quiz to get started.
              </p>
            </div>
          ) : (
            filteredQuizzes.map((quiz) => {
              const course = courseLookup?.[quiz.courseId]
              const timeLimitMinutes = quiz.timeLimitSeconds
                ? Math.round(quiz.timeLimitSeconds / 60)
                : null
              return (
                <article
                  key={quiz.id}
                  className="flex flex-col gap-3 px-4 py-5 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-900">{quiz.title}</h2>
                      <Badge variant={quiz.isActive ? 'default' : 'secondary'}>
                        {quiz.isActive ? 'Published' : 'Hidden'}
                      </Badge>
                      {quiz.maxAttempts && (
                        <Badge variant="outline">{quiz.maxAttempts} attempts</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {quiz.description || 'No description yet. Learners will appreciate a short overview.'}
                    </p>
                    <dl className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-500">
                      <div>
                        <dt className="font-medium text-gray-600">Course</dt>
                        <dd>{course?.title ?? 'Course details unavailable'}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-600">Timer</dt>
                        <dd>{timeLimitMinutes ? `${timeLimitMinutes} minutes` : 'No time limit'}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-600">Last updated</dt>
                        <dd>
                          {quiz.updatedAt
                            ? new Date(quiz.updatedAt).toLocaleString()
                            : 'Not available'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate(paths.app.quizzes.detail.build(quiz.id))
                      }
                    >
                      Manage questions
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate(paths.app.quizzes.attempts.list.build(quiz.id))
                      }
                    >
                      View attempts
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate(paths.app.quizzes.edit.build(quiz.id))
                      }
                    >
                      Edit quiz
                    </Button>
                    <Button variant="destructive" onClick={() => setQuizToDelete(quiz)}>
                      Delete
                    </Button>
                  </div>
                </article>
              )
            })
          )}
        </div>
      </section>

      <ConfirmDialog
        open={Boolean(quizToDelete)}
        title="Delete quiz?"
        description={
          quizToDelete
            ? `This will permanently remove "${quizToDelete.title}". Learners will no longer be able to access it.`
            : ''
        }
        confirmLabel="Delete quiz"
        confirmTone="destructive"
        onCancel={() => setQuizToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  )
}

interface StatTileProps {
  icon: React.ReactNode
  title: string
  value: number
  tone?: 'default' | 'success' | 'info' | 'warning'
}

function StatTile({ icon, title, value, tone = 'default' }: StatTileProps) {
  const toneClasses =
    tone === 'success'
      ? 'bg-green-50 text-green-700'
      : tone === 'info'
        ? 'bg-blue-50 text-blue-700'
        : tone === 'warning'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-gray-50 text-gray-700'

  return (
    <Card className={toneClasses}>
      <CardContent className="flex items-center gap-4 py-4">
        <div className="rounded-full bg-white/80 p-3 text-current">{icon}</div>
        <div>
          <p className="text-sm font-medium uppercase tracking-wide text-current/80">{title}</p>
          <p className="text-2xl font-bold text-current">{value}</p>
        </div>
      </CardContent>
    </Card>
  )
}
